/**
 * StrategyPage.tsx — Strategia & Margini surface orchestrator.
 *
 * Resolves the active project (route has no id → most-recent project, mirroring
 * AuditPage), then drives the make/buy strategy table + quotes inbox + RFQ
 * "prepare package" modal off REAL backend data (strategyApi hooks). Unlike the
 * gutted prototype there is no client-side computation: the server owns the
 * per-row delta and the (provisional) margin; edits are PUT back and the
 * recomputed table flows in via query cache. No fabricated numbers anywhere.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { I } from "../../components/icons";
import { apiErrorMessage } from "../../lib/api";
import {
  useDeleteQuote,
  useQuotes,
  useStrategy,
  useUpdateQuote,
  useUploadQuote,
  useUpsertCategoryStrategy,
  type CategoryStrategyInput,
  type QuoteStatus,
  type QuoteUpdateInput,
  type StrategyRow,
} from "../../lib/strategyApi";
import { useProjects } from "../../lib/tenderApi";
import { QuotesInbox } from "./QuotesInbox";
import { RFQModal } from "./RFQModal";
import { StrategyHeader } from "./StrategyHeader";
import { StrategyTable } from "./StrategyTable";

type Tab = "strategy" | "quotes";

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        border: "none",
        background: "transparent",
        padding: "12px 16px",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? "var(--c-ink)" : "var(--c-ink-3)",
        borderBottom: active ? "2px solid var(--c-brand)" : "2px solid transparent",
        marginBottom: -1,
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );
}

function CenterCard({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div style={{ padding: "20px 28px 60px" }}>
      <div
        className="card"
        style={{
          padding: 40,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function StrategyPage(): JSX.Element {
  const projectsQuery = useProjects();
  const project = projectsQuery.data?.[0];
  const pid = project?.id ?? null;

  const strategyQuery = useStrategy(pid);
  const quotesQuery = useQuotes(pid);
  const upsert = useUpsertCategoryStrategy(pid ?? "");
  const updateQuote = useUpdateQuote(pid ?? "");
  const deleteQuote = useDeleteQuote(pid ?? "");
  const uploadQuote = useUploadQuote();

  const [tab, setTab] = useState<Tab>("strategy");
  const [rfqRow, setRfqRow] = useState<StrategyRow | null>(null);

  // ── Resolve which project to show (project-level states) ──
  if (projectsQuery.isError) {
    return (
      <CenterCard>
        <div style={{ color: "var(--c-err)", fontSize: 13 }}>
          {apiErrorMessage(projectsQuery.error)}
        </div>
      </CenterCard>
    );
  }
  if (projectsQuery.isLoading) {
    return (
      <CenterCard>
        <span className="spinner" />
        <div style={{ fontSize: 13, color: "var(--c-ink-3)" }}>Caricamento progetto…</div>
      </CenterCard>
    );
  }
  if (!project) {
    return (
      <CenterCard>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: "var(--c-brand-soft)",
            color: "var(--c-brand-ink)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <I.Strategy size={20} />
        </div>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Nessun progetto</h2>
        <p style={{ margin: 0, fontSize: 13, color: "var(--c-ink-3)" }}>
          Nessun progetto — creane uno dal Workspace.
        </p>
        <Link to="/" className="btn btn-sm">
          <I.Folder size={13} /> Vai al Workspace
        </Link>
      </CenterCard>
    );
  }

  // `project` is narrowed to defined here; capture its id so the closures below
  // (and the RFQ modal) keep the non-null type.
  const activeProjectId = project.id;
  const quotes = quotesQuery.data ?? [];
  const pendingCount = quotes.filter((q) => q.status === "pending").length;

  // Report SETTLEMENT back to the row: it keeps its optimistic state only until
  // the PUT resolves, then reconciles from whatever the server actually holds.
  // The rejection is absorbed into `false` (never re-thrown — an unhandled
  // rejection here would be a console error on every failed save); it still
  // lands in `upsert.error`, which drives the banner below.
  function onCommitRow(categoryId: string, input: CategoryStrategyInput): Promise<boolean> {
    return upsert.mutateAsync({ categoryId, input }).then(
      () => true,
      () => false,
    );
  }
  function onSetQuoteStatus(id: string, status: QuoteStatus): void {
    updateQuote.mutate({ id, input: { status } });
  }
  function onEditQuoteField(id: string, patch: QuoteUpdateInput): void {
    updateQuote.mutate({ id, input: patch });
  }
  function onDeleteQuote(id: string): void {
    if (!window.confirm("Eliminare questa offerta? L'azione è permanente.")) return;
    deleteQuote.mutate(id);
  }
  function onUploadQuote(file: File): void {
    uploadQuote.mutate({ projectId: activeProjectId, file, categoryId: null });
  }

  return (
    <div style={{ padding: "20px 28px 60px" }}>
      {strategyQuery.data && <StrategyHeader totals={strategyQuery.data.totals} />}
      {strategyQuery.isLoading && (
        <div
          className="card"
          style={{
            padding: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <span className="spinner" />
          <span style={{ fontSize: 13, color: "var(--c-ink-3)" }}>Caricamento strategia…</span>
        </div>
      )}
      {strategyQuery.isError && (
        <div className="card" style={{ padding: 24, textAlign: "center" }}>
          <div style={{ color: "var(--c-err)", fontSize: 13 }}>
            {apiErrorMessage(strategyQuery.error)}
          </div>
        </div>
      )}

      {strategyQuery.data && (
        <div className="card" style={{ overflow: "hidden", marginTop: 22 }}>
          <div style={{ display: "flex", borderBottom: "1px solid var(--c-line)" }}>
            <TabBtn active={tab === "strategy"} onClick={() => setTab("strategy")}>
              Tabella di strategia
            </TabBtn>
            <TabBtn active={tab === "quotes"} onClick={() => setTab("quotes")}>
              Inbox offerte
              {pendingCount > 0 && (
                <span
                  className="pill pill-info"
                  style={{ marginLeft: 6, height: 16, fontSize: 10 }}
                >
                  {pendingCount} nuove
                </span>
              )}
            </TabBtn>
          </div>

          {(upsert.isError || updateQuote.isError || deleteQuote.isError) && (
            <div
              style={{
                padding: "8px 16px",
                background: "var(--c-err-soft)",
                color: "var(--c-err)",
                fontSize: 12,
                borderBottom: "1px solid var(--c-line)",
              }}
            >
              Salvataggio non riuscito:{" "}
              {apiErrorMessage(upsert.error ?? updateQuote.error ?? deleteQuote.error)}. Riprova.
            </div>
          )}

          {tab === "strategy" ? (
            <StrategyTable
              rows={strategyQuery.data.rows}
              onCommit={onCommitRow}
              onOpenRfq={(r) => setRfqRow(r)}
            />
          ) : (
            <QuotesInbox
              quotes={quotes}
              loading={quotesQuery.isLoading}
              uploading={uploadQuote.isPending}
              onUpload={onUploadQuote}
              onSetStatus={onSetQuoteStatus}
              onDelete={onDeleteQuote}
              onEditField={onEditQuoteField}
            />
          )}
        </div>
      )}

      {rfqRow && (
        <RFQModal
          projectId={project.id}
          projectName={project.name}
          category={{ id: rfqRow.categoryId, name: rfqRow.name }}
          onClose={() => setRfqRow(null)}
        />
      )}
    </div>
  );
}
